/**
 * UploadDropzone — drag-drop / file picker, multi-file with per-file
 * progress. Owner-agnostic: uploads go through the adapter's 3-step flow
 * (init → S3 PUT → finalize) so the same component serves the per-arche
 * library and a Workspace library.
 */

import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { AssetLibraryAdapter } from './adapter';
import { ACCEPTED_ASSET_MIMES, MAX_ASSET_UPLOAD_BYTES } from './format';

interface UploadDropzoneProps {
  adapter: AssetLibraryAdapter;
}

interface ProgressRow {
  name: string;
  status: 'uploading' | 'done' | 'error';
  error?: string;
}

export function UploadDropzone({ adapter }: UploadDropzoneProps) {
  // Destructured so the `use*` name is visible to react-hooks/rules-of-hooks
  // (a `adapter.useX()` member call is invisible to the lint rule).
  const { useUpload } = adapter;
  const uploadMutation = useUpload();
  const [tagInput, setTagInput] = React.useState('');
  const [progress, setProgress] = React.useState<ProgressRow[]>([]);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const tagInputId = `asset-tag-input-${adapter.ownerKey}`;

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const tags = tagInput
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    const next: ProgressRow[] = [];
    for (const f of Array.from(files)) {
      if (f.size > MAX_ASSET_UPLOAD_BYTES) {
        next.push({ name: f.name, status: 'error', error: 'exceeds 50 MB cap' });
        continue;
      }
      next.push({ name: f.name, status: 'uploading' });
    }
    setProgress(next);

    // Sequential to keep the UI simple. Power users use the CLI.
    for (let i = 0; i < next.length; i++) {
      if (next[i].status !== 'uploading') continue;
      const file = files.item(i);
      if (!file) continue;
      try {
        await uploadMutation.mutateAsync({ file, tags: tags.length > 0 ? tags : undefined });
        setProgress((p) =>
          p.map((row, idx) => (idx === i ? { ...row, status: 'done' as const } : row)),
        );
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setProgress((p) =>
          p.map((row, idx) => (idx === i ? { ...row, status: 'error' as const, error: msg } : row)),
        );
      }
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Upload</CardTitle>
      </CardHeader>
      <CardContent>
        <label className="block text-sm font-medium" htmlFor={tagInputId}>
          Tags (comma-separated, applied to the next batch)
        </label>
        <input
          id={tagInputId}
          type="text"
          className="mt-1 w-full rounded border border-input bg-background p-2 text-sm"
          placeholder="pack1, premium"
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
        />

        <div
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
          }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            handleFiles(e.dataTransfer.files);
          }}
          className="mt-3 cursor-pointer rounded-lg border-2 border-dashed border-input p-8 text-center hover:bg-muted/40"
          aria-label="Upload images"
        >
          <div className="text-sm font-medium">Drop images here or click to browse</div>
          <div className="text-xs text-muted-foreground">PNG, JPEG, WebP · max 50 MB each</div>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_ASSET_MIMES}
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />

        {progress.length > 0 && (
          <ul className="mt-4 space-y-1 text-sm">
            {progress.map((row, i) => (
              <li key={i} className="flex items-center justify-between">
                <span className="truncate">{row.name}</span>
                <span
                  className={
                    row.status === 'done'
                      ? 'text-green-600'
                      : row.status === 'error'
                        ? 'text-destructive'
                        : 'text-muted-foreground'
                  }
                >
                  {row.status === 'done'
                    ? 'finalized'
                    : row.status === 'error'
                      ? `failed: ${row.error}`
                      : 'uploading…'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
